/**
 * nativeNestedUtils.ts — SDK ≥1.6.3 native nested workflow 结构检测
 *
 * 职责：
 *     识别 workflow_path 含 `>` 且 depth≥1 的结构事件/容器树，供 embedded 合成跳过。
 *
 * 链路位置：
 *     embeddedSubworkflowUtils.shouldUseEmbeddedSynthesis → workflowReducer.applyStructureEvent。
 *
 * 当前裁剪范围：
 *     仅 D29 nested native vs embedded 分流；不含 sibling-nested 专用逻辑。
 */
import type { LangchainAgentEvent } from '../../types/events';
import type { WorkflowContainerTreeState } from '../../types/workflowContainer';
import { buildContainerId } from './workflowContainerProjector';
import { isWorkflowStructureEventType } from '../workflowStructureEvents';
import {
  isWorkflowPathEqualOrUnder,
  lastWorkflowPathSegment,
  parentWorkflowPath,
  splitWorkflowPathSegments,
} from './workflowPathUtils';

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

function readNumber(data: Record<string, unknown>, key: string): number | undefined {
  const value = data[key];
  return typeof value === 'number' ? value : undefined;
}

/** 单条结构事件是否携带 SDK native nested path（depth≥1 且 workflow_path 含 `>`）。 */
export function isNativeNestedStructureEvent(event: LangchainAgentEvent): boolean {
  if (!isWorkflowStructureEventType(event.event_type)) return false;
  const data = (event.data ?? {}) as Record<string, unknown>;
  const path = readString(data, 'workflow_path') ?? '';
  const depth = readNumber(data, 'workflow_depth') ?? 0;
  return depth >= 1 && path.includes('>');
}

/** 事件批是否含 native nested 结构 open（D29 fixture / replay 检测）。 */
export function hasNativeNestedStructureEvents(events: LangchainAgentEvent[]): boolean {
  return events.some((event) => isNativeNestedStructureEvent(event));
}

/**
 * 子 workflow_path 是否由「父路径 + 同名 stage」承载（native nested 典型形态）。
 * 例：`demo-nested>parallel_review` ↔ `demo-nested|stage:parallel_review`。
 * 多层：`a>b>c` ↔ `a>b|stage:c`（按完整 segment，不只切第一个 `>`）。
 */
export function resolveHostStageContainerId(
  tree: WorkflowContainerTreeState,
  workflowPath: string,
): string | null {
  const parentPath = parentWorkflowPath(workflowPath);
  const childSegment = lastWorkflowPathSegment(workflowPath);
  if (!parentPath || !childSegment) return null;

  const stageId = buildContainerId(parentPath, 'stage', childSegment);
  return tree.containersById[stageId] != null ? stageId : null;
}

function isHostStageNestedChildPath(
  tree: WorkflowContainerTreeState,
  workflowPath: string,
): boolean {
  return resolveHostStageContainerId(tree, workflowPath) != null;
}

/**
 * 容器树是否已由 SDK 投影出 native nested 路径。
 */
export function hasNativeNestedStructureInTree(
  tree: WorkflowContainerTreeState,
): boolean {
  for (const node of Object.values(tree.containersById)) {
    if (node.workflow_depth < 1) continue;
    if (!node.workflow_path.includes('>')) continue;
    if (isHostStageNestedChildPath(tree, node.workflow_path)) {
      return true;
    }
  }
  return false;
}

function clearHostStageLoopSession(
  tree: WorkflowContainerTreeState,
  hostStageId: string | null,
): WorkflowContainerTreeState {
  if (!hostStageId) return tree;
  const hostStage = tree.containersById[hostStageId];
  if (!hostStage || hostStage.scope !== 'stage' || !hostStage.loopSessionId) return tree;

  return {
    ...tree,
    containersById: {
      ...tree.containersById,
      [hostStageId]: { ...hostStage, loopSessionId: null },
    },
  };
}

/** stage 下已有 subworkflow 容器 → 宿主 stage 仅作壳，不承载 scoped loop。 */
export function isNestedHostStageShell(
  tree: WorkflowContainerTreeState,
  stageNode: WorkflowContainerRuntimeNode,
): boolean {
  if (stageNode.scope !== 'stage') return false;
  return Object.values(tree.containersById).some(
    (node) => node.scope === 'subworkflow' && node.parent_container_id === stageNode.container_id,
  );
}

/**
 * subworkflow-start / parallel-item-start：清宿主 stage 上误绑的 loopSessionId。
 * 内层 Agent loop 应落在 parallel item / aggregate 上。
 */
export function clearHostStageLoopOnNestedChildOpen(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
): WorkflowContainerTreeState {
  const eventType = event.event_type;
  if (eventType !== 'subworkflow-start' && eventType !== 'parallel-item-start') return tree;

  const data = (event.data ?? {}) as Record<string, unknown>;
  const workflowPath = readString(data, 'workflow_path');
  if (!workflowPath) return tree;

  if (eventType === 'subworkflow-start') {
    const childWorkflowId = readString(data, 'child_workflow_id');
    if (!childWorkflowId) return tree;

    const subId = buildContainerId(workflowPath, 'subworkflow', childWorkflowId);
    const sub = tree.containersById[subId];
    const hostStageId =
      sub?.parent_container_id ?? resolveHostStageContainerId(tree, workflowPath);
    return clearHostStageLoopSession(tree, hostStageId);
  }

  return clearHostStageLoopSession(tree, resolveHostStageContainerId(tree, workflowPath));
}

/** @deprecated 使用 clearHostStageLoopOnNestedChildOpen */
export function clearHostStageLoopOnSubworkflowStart(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
): WorkflowContainerTreeState {
  return clearHostStageLoopOnNestedChildOpen(tree, event);
}

/** native subworkflow-start 时丢弃同宿主 stage 下已过时的 embedded 合成子树。 */
export function dropStaleEmbeddedPathsOnNativeSubworkflowStart(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
): WorkflowContainerTreeState {
  if (event.event_type !== 'subworkflow-start' || !isNativeNestedStructureEvent(event)) {
    return tree;
  }

  const data = (event.data ?? {}) as Record<string, unknown>;
  const nativeChildPath = readString(data, 'workflow_path');
  if (!nativeChildPath) return tree;

  const rootAnchor = splitWorkflowPathSegments(nativeChildPath)[0];
  if (!rootAnchor) return tree;

  let changed = false;
  const containersById = { ...tree.containersById };
  let activeContainerIds = [...tree.activeContainerIds];

  for (const [id, node] of Object.entries(tree.containersById)) {
    if (node.workflow_depth < 1) continue;
    if (!node.workflow_path.startsWith(`${rootAnchor}>`)) continue;
    if (
      node.workflow_path === nativeChildPath
      || isWorkflowPathEqualOrUnder(node.workflow_path, nativeChildPath)
    ) {
      continue;
    }
    // 保留其他合法 native 子路径（sibling-nested：stage_a / stage_b 并存）
    if (isHostStageNestedChildPath(tree, node.workflow_path)) continue;
    delete containersById[id];
    activeContainerIds = activeContainerIds.filter((cid) => cid !== id);
    changed = true;
  }

  return changed ? { ...tree, containersById, activeContainerIds } : tree;
}
